<template>
  <div class="fee-list">
    <div class="fee-rule">
      <div class="title">
        {{ $t('EvCharging.ManagementFeeList.RulesTitle') }}
      </div>
      <ul>
        <li>{{ $t('EvCharging.ManagementFeeList.Rule1') }}</li>
        <li v-if="chargingFeature.SystemUsageFeeByPowerUsage">
          {{ $t('EvCharging.ManagementFeeList.Rule2') }}
        </li>
        <li>
          {{ $t('EvCharging.ManagementFeeList.Rule3') }}
          (<span
            class="introduction"
            @click="$toExternaLink('https://knst.tw/727xnc')"
          >{{ $t("General.Instructions") }}</span>)
        </li>
      </ul>
    </div>
    <!-- Filter -->
    <FilterFrame
      :filter-form="periodFilter"
      :auto-search="true"
      class="filter-frame"
      @search="search"
      @clear="clearSearch"
    />
    <div class="function">
      <div class="right">
        <span class="result">{{ $t('EvCharging.TotalCount', { count: totalCount }) }}</span>
      </div>
    </div>
    <ManagementTabPanel
      v-if="!chargingFeature.SystemUsageFeeByPowerUsage"
      :action-name="$t('EvCharging.ManagementFeeList.Download')"
      :table-structure="periodFeeStructure"
      :loading="isLoading"
      :is-tab-loading="isTabLoading"
      :data="chargingFeeData"
      @download="clickDownloadBtn"
    />
    <!-- TAB -->
    <el-tabs v-else :active-name="activeTabName" type="card" class="management_tabs" @tab-click="handleTabClick">
      <!-- 產單期別 -->
      <el-tab-pane :label="$t('EvCharging.ManagementFeeList.PerKwhPeriod')" name="perKwhPeriod">
        <!-- Filter -->
        <ManagementTabPanel
          :action-name="$t('EvCharging.ManagementFeeList.Download')"
          :table-structure="periodFeeStructure"
          :loading="isLoading"
          :is-tab-loading="isTabLoading"
          :data="chargingFeeData"
          @download="clickDownloadBtn"
        />
      </el-tab-pane>
      <!-- 系統使用費期別 -->
      <el-tab-pane :label="$t('EvCharging.ManagementFeeList.PerKwhUsedFee')" name="PerKwhUsedFee">
        <ManagementTabPanel
          :action-name="$t('EvCharging.ManagementFeeList.DownloadDetail')"
          :table-structure="usedSystemFeeStructure"
          :loading="isLoading"
          :is-tab-loading="isTabLoading"
          :data="chargingFeeData"
          @download="downloadManagementFeeStatementsDetails"
        />
      </el-tab-pane>
    </el-tabs>
    <el-pagination
      background
      layout="total, prev, pager, next, jumper"
      :total="totalCount"
      @current-change="getList"
    >
    </el-pagination>
    <el-dialog
      class="management-dialog"
      title="下載產單報表"
      :visible.sync="dialogVisible"
      :close-on-click-modal="false"
      width="600px"
    >
      <div class="content">
        下載後，請到管理費功能「新增期別」內上傳檔案。
      </div>
      <div class="dialog-buttons">
        <el-button
          @click="dialogVisible = false"
        >
          取消
        </el-button>
        <el-button
          type="primary"
          @click="downloadManagementFeeImportTemplate(selectedPeriod)"
        >
          確定下載
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import FilterFrame from '@/components/FilterFrame'
import ManagementTabPanel from '../components/ManagementTabPanel.vue'
import { apiEvCharging } from '@/api/EvCharging'
import dayjs from 'dayjs'

export default {
  components: {
    FilterFrame,
    ManagementTabPanel
  },
  props: {
    chargingFeature: {
      type: Object,
      required: true
    }
  },
  data () {
    const maxCount = 10
    const defaultReq = {
      'Paging.Offset': 0,
      'Paging.MaxCount': 10
    }
    const req = { ...defaultReq }
    const isLoading = false // for table data
    return {
      dialogVisible: false,
      periodFeeStructure: {
        Period: {
          label: this.$t('EvCharging.ManagementFeeList.ManagementPerStationFeePeriod'),
          minWidth: 100
        },
        AccountReceivableAmount: {
          label: this.$t('EvCharging.ManagementFeeList.ResidentFee'),
          minWidth: 140
        }
      },
      usedSystemFeeStructure: {
        Period: {
          label: this.$t('EvCharging.ManagementFeeList.ClaimDetailPeriod'),
          minWidth: 140
        },
        TotalChargingKWH: {
          label: this.$t('EvCharging.ManagementFeeList.ChargingAmount'),
          minWidth: 140
        },
        SystemUsageFeeAmount: {
          label: this.$t('EvCharging.ManagementFeeList.SystemUsageFee'),
          minWidth: 140,
          tooltip: true,
          toolContent: this.$t('EvCharging.SystemUsageFeeTooltip', { SystemUsageFeeRate: this.chargingFeature.SystemUsageFeeRate })
        },
        InvoiceDate: {
          label: this.$t('EvCharging.ManagementFeeList.InvoiceIssuedDate'),
          minWidth: 140
        },
        InvoiceSn: {
          label: this.$t('EvCharging.ManagementFeeList.InvoiceSerialNumber'),
          minWidth: 140
        }
      },
      chargingFeeData: [],
      totalCount: 0,
      defaultReq,
      req,
      isLoading,
      isTabLoading: false,
      maxCount,
      periodFilter: [
        {
          label: this.chargingFeature.SystemUsageFeeByPowerUsage ? this.$t('EvCharging.ManagementFeeList.Period') : this.$t('EvCharging.ManagementFeeList.ManagementPeriod'),
          name: 'Period',
          type: 'input',
          value: ''
        }
      ],
      activeTabName: 'perKwhPeriod',
      selectedPeriod: '',
      hasAlerted: false
    }
  },
  methods: {
    handleTabClick (tab) {
      // 管理費已度計費會有兩個分頁，在分頁切換事table 會有閃爍，因此用一個loading 提升使用者體驗
      // 如果點擊的 tab 和當前選中的 tab 不同，才觸發 loading
      if (tab.name !== this.activeTabName) {
        this.isTabLoading = true
        this.activeTabName = tab.name // 更新 activeTabName
        setTimeout(() => {
          this.isTabLoading = false
        }, 300)
      }
    },
    getList (page = 1) {
      // 清空列表且呈現 loading 畫面
      this.inputLogList = []
      this.isLoading = true
      this.req['Paging.Offset'] = (page - 1) * 10
      apiEvCharging.ManagementFeeList.getManagementFeeStatements(this.req).then(res => {
        if (res?.data) {
          this.totalCount = res.data.TotalCount
          this.chargingFeeData = res.data.Items
          // 整管理費 每月產單更新提醒
          const currentPeriod = dayjs().format('YYYY-MM')
          const currentData = res.data.Items.find(item => item.Period === currentPeriod)
          if (currentData && currentData.DownloadCount === 0 && !this.hasAlerted) {
            this.hasAlerted = true
            this.$alert(this.$t('EvCharging.HaveNewManagementFeeAlertBody'), this.$t('EvCharging.HaveNewManagementFeeAlertTitle'), {
              confirmButtonText: this.$t('General.OK'),
              type: 'warning',
              callback: action => {}
            })
          }
        }
      }).catch(err => {
        console.log(err)
      }).finally(() => {
        this.isLoading = false
      })
    },
    search (filterOptions) {
      this.req = { ...filterOptions, 'Paging.MaxCount': this.maxCount }
      this.getList()
    },
    clickDownloadBtn (period) {
      this.dialogVisible = true
      this.selectedPeriod = period
    },
    downloadManagementFeeStatementsDetails (period) {
      this.selectedPeriod = period
      const config = {
        headers: {
          'content-type': 'application/x-www-form-urlencoded'
        },
        responseType: 'blob' // important
      }
      apiEvCharging.ManagementFeeList.getManagementFeeStatementsDetailsExcel(period, config).then(res => {
        this.dialogVisible = false
        this.$downloadFile(res.data, `${decodeURIComponent(res.headers['content-disposition'].split(';')[2].replace(/"/g, '').replace("filename*=UTF-8''", '')).trim()}`)
      }).catch(err => {
        console.log(err)
      })
    },
    downloadManagementFeeImportTemplate (period) {
      const config = {
        headers: {
          'content-type': 'application/x-www-form-urlencoded'
        },
        responseType: 'blob' // important
      }
      apiEvCharging.ManagementFeeList.getManagementFeeStatementsImportTemplateExcel(period, config).then(res => {
        this.dialogVisible = false
        this.$downloadFile(res.data, `${decodeURIComponent(res.headers['content-disposition'].split(';')[2].replace(/"/g, '').replace("filename*=UTF-8''", '')).trim()}`)
      }).catch(err => {
        console.log(err)
      })
    },
    clearSearch () {
      this.req = { ...this.defaultReq }
      this.getList()
    }
  }
}
</script>

<style lang="scss" scoped>
.filter_frame{
  margin-top: 10px;
}
.function {
    width: 100%;
    display: flex;
    justify-content: right;
    align-items: center;
    padding-block: 20px;
    .right {
    .result {
        margin-left: 12px;
        font-size: map-get($fontSize, medium);
        color: $mainFontColor;
    }
    }
}
.fee-list {
.fee-rule {
    text-align: left;
    margin-block: 24px;
    .title {
    font-size: map-get($fontSize, large);
    font-weight: bold;
    color: $mainFontColor;
    }
    ul {
    margin-top: 8px;
    padding-inline-start: 20px;
      li {
          list-style: disc;
          line-height: 1.5pc;
      }
    }
    .introduction {
      color: $mainColor;
      cursor: pointer;
      &:hover {
        text-decoration: underline;
      }
    }
}
::v-deep .el-checkbox.is-disabled {
    display: none;
}
.el-pagination {
    display: flex;
    justify-content: flex-start;
    margin-top: 28px;
}
}

.management_tabs {
  margin-bottom: 10px;
  :deep(.el-tabs__header) {
    border-bottom: 1px solid $mainColor;
  }

  :deep(.el-tabs__nav){
    border: none;
  }

  :deep(.el-tabs__item) {
    border-bottom-color: $mainColor;
    border-top: 4px solid $whiteHover;
    border-left: none;
    margin-left: 5px;
    background-color: $secondTabBg;
    font-weight: 500;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
  }

  :deep(.el-tabs__item.is-active) {
    border: 1px solid $mainColor;
    border-top: 4px solid $mainColor;
    border-bottom-color: transparent;
    background-color: $white;
    font-weight: bold;
    color: #484848 !important;
  }
}
.management-dialog{
  :deep(.el-dialog__header){
    display: flex;
    font-size: 24px;
  }
  :deep(.el-dialog__body){
    padding: 20px;
    text-align: left;
  }
  .content {
    font-size: 16px;
    padding-bottom: 20px;
  }
  .dialog-buttons{
    display: flex;
    justify-content: flex-end;
  }
}
</style>
